import { useState } from 'react'
import { StatusBadge } from '../ui/StatusBadge'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { Image as ImageIcon, ExternalLink, MessageSquareDot, FolderPlus } from 'lucide-react'
import type { VisualTask, GeneratedImage } from '../../lib/api'
import { getImageAssetUrl } from '../../lib/api'
import { AddToCollectionModal } from './AddToCollectionModal'

interface VisualTaskCardProps {
  task: VisualTask
  images?: GeneratedImage[]
  onClick?: () => void
  /** Collection ids this task already belongs to (for modal indicator). */
  memberCollectionIds?: string[]
}

export function VisualTaskCard({
  task,
  images = [],
  onClick,
  memberCollectionIds = [],
}: VisualTaskCardProps) {
  const firstImage = images.length > 0 ? images[0] : null
  const hasActiveSession = images.some((img) => img.source_image_id)
  const [collectionModalOpen, setCollectionModalOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className="card hover:shadow-lg transition-shadow cursor-pointer w-full text-left"
      >
        {/* Thumbnail or Placeholder */}
        <div className="mb-4 h-48 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center relative">
          {firstImage?.id ? (
            <img
              src={getImageAssetUrl(firstImage.id)}
              alt="Generated visual"
              className="w-full h-full object-cover"
              onError={(e) => {
                if (firstImage.public_url) {
                  ;(e.target as HTMLImageElement).src = firstImage.public_url
                }
              }}
            />
          ) : firstImage?.public_url ? (
            <img
              src={firstImage.public_url}
              alt="Generated visual"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center text-slate-400">
              <ImageIcon className="w-12 h-12 mx-auto mb-2" />
              <p className="text-sm">
                {task.status === 'pending' || task.status === 'running'
                  ? 'Generando...'
                  : 'Sin imagen'}
              </p>
            </div>
          )}
          {hasActiveSession && (
            <div
              className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-600 text-white text-xs font-medium shadow"
              title="Esta tarea tiene ediciones activas"
            >
              <MessageSquareDot className="w-3 h-3" />
              Sesion activa
            </div>
          )}
        </div>

        {/* Task Info */}
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <StatusBadge status={task.status} />
            </div>
            {images.length > 0 && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {images.length}
              </span>
            )}
          </div>

          {/* Channel */}
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">
              {task.channel}
            </span>
            {task.template_id && (
              <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                Template
              </span>
            )}
          </div>

          {/* Prompt Preview */}
          {task.prompt_data?.prompt_text && (
            <p className="text-sm text-slate-600 line-clamp-2">
              {task.prompt_data.prompt_text}
            </p>
          )}

          {/* Metadata */}
          <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
            <span>
              {formatDistanceToNow(new Date(task.created_at), {
                addSuffix: true,
                locale: es,
              })}
            </span>
            <div className="flex items-center gap-2">
              {task.content_id && <ExternalLink className="w-3 h-3" />}
              <button
                type="button"
                title="Agregar a coleccion"
                onClick={(e) => {
                  e.stopPropagation()
                  setCollectionModalOpen(true)
                }}
                className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-emerald-600 transition-colors"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </button>

      <AddToCollectionModal
        isOpen={collectionModalOpen}
        onClose={() => setCollectionModalOpen(false)}
        taskId={task.id}
        memberCollectionIds={memberCollectionIds}
      />
    </>
  )
}
